/**
 * ══════════════════════════════════════════════════════════════════════════════
 * PTEL DESIGN TOKENS v1.0
 * ══════════════════════════════════════════════════════════════════════════════
 * 
 * Tokens de diseño centralizados para todas las herramientas PTEL.
 * Importar estos valores en lugar de usar valores hardcodeados.
 * 
 * Uso:
 *   import { colors, typography, spacing } from './ptel-design-tokens';
 * 
 * ══════════════════════════════════════════════════════════════════════════════
 */

// ─────────────────────────────────────────────────────────────────────────────
// COLORES
// ─────────────────────────────────────────────────────────────────────────────

export const colors = {
  // Colores semánticos (invariantes)
  primary: '#2DD4BF',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  
  // Modo oscuro
  dark: {
    background: '#1E1E1C',
    backgroundSecondary: '#262624',
    backgroundTertiary: '#2E2E2C',
    backgroundHover: '#1a1a18',
    foreground: '#fafafa',
    foregroundSecondary: '#a1a1aa',
    foregroundMuted: '#71717a',
    foregroundSubtle: '#52524f',
    foregroundDisabled: '#3f3f3c',
    border: '#3f3f3c',
    borderLight: '#333330',
  },
  
  // Modo claro
  light: {
    background: '#ffffff',
    backgroundSecondary: '#f9fafb',
    backgroundTertiary: '#f3f4f6',
    backgroundHover: '#e5e7eb',
    foreground: '#18181b',
    foregroundSecondary: '#71717a',
    foregroundMuted: '#a1a1aa',
    foregroundSubtle: '#d4d4d8',
    foregroundDisabled: '#e4e4e7',
    border: '#e4e4e7',
    borderLight: '#f4f4f5',
  },
  
  // Categorías TextLearning
  categories: {
    falsePositives: '#F87171',  // Coral
    abbreviations: '#A78BFA',   // Violeta
    dictionary: '#2DD4BF',      // Turquesa
    corrections: '#60A5FA',     // Azul
    suggestions: '#F472B6',     // Rosa
  },
  
  // Tipologías PTEL
  typologies: {
    SAN: { color: '#ef4444', name: 'Sanitario' },
    EDU: { color: '#22c55e', name: 'Educativo' },
    POL: { color: '#3b82f6', name: 'Policial' },
    CUL: { color: '#a855f7', name: 'Cultural' },
    REL: { color: '#8b5cf6', name: 'Religioso' },
    EME: { color: '#f97316', name: 'Emergencias' },
    DEP: { color: '#84cc16', name: 'Deportivo' },
    ADM: { color: '#06b6d4', name: 'Administrativo' },
    VIA: { color: '#fbbf24', name: 'Vial' },
    IND: { color: '#78716c', name: 'Industrial' },
  },
  
  // Colores de score (unificados con Plan v4.1)
  // Ref: INVESTIGACION_MULTIPERSPECTIVA_PTEL_v2_1.md
  scores: {
    confirmed: '#56F000',  // 90-100: CONFIRMED (Verde)
    high: '#2DCCFF',       // 75-89:  HIGH (Azul)
    medium: '#FCE83A',     // 60-74:  MEDIUM (Amarillo)
    low: '#FFB302',        // 40-59:  LOW (Naranja)
    critical: '#FF3838',   // <40:    CRITICAL (Rojo)
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// TIPOGRAFÍA
// ─────────────────────────────────────────────────────────────────────────────

export const typography = {
  // Familias
  fontFamily: {
    sans: "'Space Grotesk', system-ui, -apple-system, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
  },
  
  // Tamaños (rem)
  fontSize: {
    '2xs': '0.625rem',   // 10px
    xs: '0.75rem',       // 12px
    sm: '0.875rem',      // 14px
    base: '1rem',        // 16px
    lg: '1.125rem',      // 18px
    xl: '1.25rem',       // 20px
    '2xl': '1.5rem',     // 24px
  },
  
  // Pesos
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  
  // Line heights
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// ESPACIADO
// ─────────────────────────────────────────────────────────────────────────────

export const spacing = {
  0: '0',
  0.5: '2px',
  1: '4px',
  1.5: '6px',
  2: '8px',
  2.5: '10px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// BORDES Y RADIOS
// ─────────────────────────────────────────────────────────────────────────────

export const borderRadius = {
  none: '0',
  sm: '4px',
  DEFAULT: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '20px',
  full: '9999px',
} as const;

export const borderWidth = {
  DEFAULT: '1px',
  1.5: '1.5px',
  2: '2px',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// SOMBRAS
// ─────────────────────────────────────────────────────────────────────────────

export const shadows = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  DEFAULT: '0 1px 3px rgba(0, 0, 0, 0.1)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1)',
  lg: '0 8px 32px rgba(0, 0, 0, 0.2)',
  xl: '0 20px 40px rgba(0, 0, 0, 0.3)',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// TRANSICIONES
// ─────────────────────────────────────────────────────────────────────────────

export const transitions = {
  fast: '150ms ease-out',
  normal: '200ms ease-out',
  slow: '300ms ease-out',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// BREAKPOINTS
// ─────────────────────────────────────────────────────────────────────────────

export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// CONTENEDORES
// ─────────────────────────────────────────────────────────────────────────────

export const containers = {
  header: '1152px',      // max-w-6xl
  stepper: '1024px',     // max-w-5xl
  main: '896px',         // max-w-4xl
  modalSm: '384px',      // max-w-sm
  modalMd: '448px',      // max-w-md
  modalLg: '672px',      // max-w-2xl
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// DIMENSIONES DE COMPONENTES
// ─────────────────────────────────────────────────────────────────────────────

export const components = {
  // Header
  header: {
    height: '56px',
    logoSize: '32px',
  },
  
  // Botones
  button: {
    xs: { height: '24px', paddingX: '8px', fontSize: '10px', iconSize: '12px' },
    sm: { height: '32px', paddingX: '12px', fontSize: '12px', iconSize: '14px' },
    md: { height: '40px', paddingX: '16px', fontSize: '14px', iconSize: '16px' },
    lg: { height: '48px', paddingX: '20px', fontSize: '16px', iconSize: '18px' },
  },
  
  // Inputs
  input: {
    sm: { height: '32px', paddingX: '12px', paddingY: '8px', fontSize: '12px' },
    md: { height: '40px', paddingX: '12px', paddingY: '10px', fontSize: '14px' },
    lg: { height: '48px', paddingX: '16px', paddingY: '12px', fontSize: '16px' },
  },
  
  // Badges
  badge: {
    xs: { height: '18px', paddingX: '6px', paddingY: '2px', fontSize: '10px' },
    sm: { height: '22px', paddingX: '8px', paddingY: '4px', fontSize: '11px' },
    md: { height: '26px', paddingX: '10px', paddingY: '4px', fontSize: '12px' },
  },
  
  // Iconos
  icon: {
    xs: '10px',
    sm: '12px',
    md: '14px',
    lg: '16px',
    xl: '18px',
    '2xl': '20px',
    '3xl': '24px',
    '4xl': '32px',
  },
  
  // Stepper
  stepper: {
    circleSize: '24px',
    connectorWidth: '48px',
    connectorHeight: '2px',
  },
  
  // Filas de lista
  listItem: {
    paddingNormal: '12px',
    paddingCompact: '8px',
    actionButtonNormal: '28px',
    actionButtonCompact: '24px',
  },
  
  // Modal
  modal: {
    padding: '20px',
    headerIconSize: '40px',
  },
  
  // Toast
  toast: {
    paddingX: '16px',
    paddingY: '12px',
  },
  
  // Info Panel
  infoPanel: {
    padding: '16px',
    iconContainerSize: '36px',
    bottomOffset: '24px',
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// OPACIDADES ESTÁNDAR
// ─────────────────────────────────────────────────────────────────────────────

export const opacity = {
  badgeBg: 0.15,
  hoverSubtle: 0.08,
  active: 0.20,
  modalOverlay: 0.6,
  glassmorphism: 0.7,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Z-INDEX
// ─────────────────────────────────────────────────────────────────────────────

export const zIndex = {
  dropdown: 50,
  sticky: 50,
  modal: 100,
  toast: 200,
  tooltip: 200,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// UTILIDAD: Hex a RGBA
// ─────────────────────────────────────────────────────────────────────────────

export const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// ─────────────────────────────────────────────────────────────────────────────
// UTILIDAD: Color de Score
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Obtiene el color según la puntuación de validación
 * Umbrales alineados con Plan Sistemático PTEL v4.1
 */
export const getScoreColor = (score: number): string => {
  if (score >= 90) return colors.scores.confirmed;  // CONFIRMED
  if (score >= 75) return colors.scores.high;       // HIGH
  if (score >= 60) return colors.scores.medium;     // MEDIUM
  if (score >= 40) return colors.scores.low;        // LOW
  return colors.scores.critical;                    // CRITICAL
};

/**
 * Obtiene el nivel de confianza según la puntuación
 */
export type ScoreLevel = 'confirmed' | 'high' | 'medium' | 'low' | 'critical';

export const getScoreLevel = (score: number): ScoreLevel => {
  if (score >= 90) return 'confirmed';
  if (score >= 75) return 'high';
  if (score >= 60) return 'medium';
  if (score >= 40) return 'low';
  return 'critical';
};

/**
 * Obtiene la etiqueta legible del nivel de score
 */
export const getScoreLabel = (score: number): string => {
  if (score >= 90) return 'Confirmado';
  if (score >= 75) return 'Alto';
  if (score >= 60) return 'Medio';
  if (score >= 40) return 'Bajo';
  return 'Crítico';
};

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT DEFAULT
// ─────────────────────────────────────────────────────────────────────────────

const designTokens = {
  colors,
  typography,
  spacing,
  borderRadius,
  borderWidth,
  shadows,
  transitions,
  breakpoints,
  containers,
  components,
  opacity,
  zIndex,
  hexToRgba,
  getScoreColor,
  getScoreLevel,
  getScoreLabel,
};

export default designTokens;
